import { request as apiRequest } from '@/lib/apiClient'

async function apiFetch<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const { data, error } = await apiRequest<T>(method, path, body)
  if (error) throw new Error(error.message)
  return data as T
}

export interface EngagementSummary {
  id:                   string
  name:                 string
  client_name:          string | null
  scope:                string | null
  surveyor_hardware_id: string
  surveyor_email:       string | null
  started_at:           string
  ended_at:             string | null
  submitted_at:         string
  status:               string
  assets_count:         number
  attack_runs_count:    number
}

export interface EngagementAsset {
  id:                   string
  host_ip:              string
  host_name:            string | null
  mac_address:          string | null
  vendor:               string | null
  is_online:            boolean | null
  ping_time:            number | null
  scan_time:            string | null
  industrial_vendor:    string | null
  industrial_category:  string | null
  // comma-joined string from migration 013, split at render time
  industrial_protocols: string | null
  hardware_id:          string | null
  machine_name:         string | null
  // Phase 2C-B server columns (migration 014). populated only when
  // Dorothy ships them in the asset payload, empty until NI Asset
  // writer lands (backlog 29.5). today these stay null on Dorothy
  // submits. row expander reads device data from topology_snapshot
  // JSONB by host_ip lookup instead
  device_type:             string | null
  device_type_confidence:  string | null
  device_vendor:           string | null
  device_model:            string | null
  device_firmware_version: string | null
  // migration 016 columns: subnet attribution + how Dorothy found it.
  // `subnet_cidr` may be null on legacy rows from before the
  // migration ran. UI should fall back to a /24 of host_ip
  subnet_cidr:             string | null
  discovery_source:        string | null
  // migration 017: Dorothy-side stable id (preserved on submit so
  // findings/evidence can reference assets cross-table)
  external_asset_id?:      string | null
  created_at:           string
}

export interface EngagementSubnet {
  id:               string
  cidr:             string
  discovery_source: string
  host_count:       number
  classified_count: number
  high_risk_count:  number
  attacked_count:   number
  created_at:       string
}

// server returns nested asset summary on findings/evidence so the UI
// can render the affected-host cell without a per-row lookup. `asset`
// is null when the row couldn't be linked to an engagement_assets row
// (Dorothy supplied a host_ip that matched nothing, or no external
// id was provided)
export interface EngagementAssetSummary {
  id:          string
  host_ip:     string | null
  host_name:   string | null
  subnet_cidr: string | null
  device_type: string | null
}

export interface EngagementFinding {
  id:                  string
  asset_id:            string | null
  external_asset_id:   string | null
  title:               string
  severity:            string
  category:            string | null
  status:              string
  recommendation:      string | null
  evidence_ids:        string[] | null
  raw_json:            any | null
  created_at:          string
  updated_at:          string
  asset:               EngagementAssetSummary | null
}

export interface EngagementEvidence {
  id:                  string
  asset_id:            string | null
  external_asset_id:   string | null
  evidence_type:       string
  source_tool:         string | null
  summary:             string | null
  severity_hint:       string | null
  raw_json:            any | null
  collected_at:        string | null
  asset:               EngagementAssetSummary | null
}

export interface EngagementSession {
  id:                  string
  external_session_id: string | null
  session_name:        string | null
  started_at:          string | null
  ended_at:            string | null
  source_nic:          string | null
  selected_subnets:    any | null
  host_count:          number
  evidence_count:      number
  finding_count:       number
  attack_run_count:    number
  submitted_at:        string | null
}

export interface EngagementPort {
  id:            string
  asset_id:      string | null
  host_ip:       string
  port:          number
  protocol:      string | null
  service:       string | null
  banner:        string | null
  severity:      string | null
  scan_time:     string | null
  created_at:    string
}

export interface EngagementAttackRun {
  id:               string
  attack_type:      string | null
  protocol:         string | null
  source_ip:        string | null
  source_mac:       string | null
  target_ip:        string | null
  target_mac:       string | null
  target_port:      number | null
  target_rate_mbps: number | null
  packets_sent:     number | null
  duration_seconds: number | null
  start_time:       string | null
  stop_time:        string | null
  hardware_id:      string | null
  machine_name:     string | null
  // migration 018: per-run packet-rate telemetry from Dorothy.
  // null on legacy submissions (pre-Dorothy-PPS). UI falls back to
  // packets / duration for an approximation there. Peak/Target
  // never get faked, they show "—" when null
  current_pps?:     number | null
  average_pps?:     number | null
  peak_pps?:        number | null
  target_pps?:      number | null
  // migration 019: manual operator review fields. SEACUREDB-side
  // annotation, never set by Dorothy
  observed_reaction?: string | null
  reviewed_by?:       string | null
  reviewed_at?:       string | null
  created_at:       string
}

export interface TopologyNode {
  data: {
    id:             string
    label?:         string
    type:           string  // Self|Gateway|Host|RemoteHost|SubnetCloud|UnknownHop
    status?:        string
    isLocal?:       string
    isInternet?:    string
    scanStatus?:    string
    expanded?:      string
    stale?:         boolean
    lastProbedAt?:  string
    lastProbeLevel?: string
    [key: string]: any
  }
}

export interface TopologyEdge {
  data: {
    source: string
    target: string
    type:   string  // Flow|ArpSeen|RouteHint|Structural|TraceroutePath|SnmpNeighbor
    bytes?: number
    [key: string]: any
  }
}

export interface TopologySnapshot {
  nodes: TopologyNode[]
  edges: TopologyEdge[]
}

export interface EngagementDetail extends EngagementSummary {
  notes:             string | null
  topology_snapshot: TopologySnapshot | null
  assets:            EngagementAsset[]
  ports:             EngagementPort[]
  attack_runs:       EngagementAttackRun[]
  subnets:           EngagementSubnet[]
  findings:          EngagementFinding[]
  evidence:          EngagementEvidence[]
  session:           EngagementSession | null
}

export interface EngagementStats {
  total_engagements:      number
  engagements_this_month: number
  total_hosts_assessed:   number
  last_submitted_at:      string | null
}

export const engagementsApi = {
  async list(surveyorHwid?: string): Promise<EngagementSummary[]> {
    const qs = surveyorHwid
      ? `?surveyor_hardware_id=${encodeURIComponent(surveyorHwid)}`
      : ''
    const res = await apiFetch<{ engagements: EngagementSummary[] }>(
      'GET', `/api/engagements${qs}`
    )
    return res.engagements
  },

  async detail(id: string): Promise<EngagementDetail> {
    const res = await apiFetch<{
      engagement: Omit<EngagementDetail, 'assets' | 'ports' | 'attack_runs' | 'subnets' | 'findings' | 'evidence' | 'session'>
      assets:      EngagementAsset[]
      ports:       EngagementPort[]
      attack_runs: EngagementAttackRun[]
      subnets?:    EngagementSubnet[]
      findings?:   EngagementFinding[]
      evidence?:   EngagementEvidence[]
      session?:    EngagementSession | null
    }>('GET', `/api/engagements/${id}`)
    return {
      ...res.engagement,
      assets:            res.assets,
      ports:             res.ports,
      attack_runs:       res.attack_runs,
      subnets:           res.subnets  ?? [],
      findings:          res.findings ?? [],
      evidence:          res.evidence ?? [],
      session:           res.session  ?? null,
      topology_snapshot: res.engagement.topology_snapshot ?? null,
    }
  },

  async stats(surveyorHwid?: string): Promise<EngagementStats> {
    const qs = surveyorHwid
      ? `?surveyor_hardware_id=${encodeURIComponent(surveyorHwid)}`
      : ''
    return apiFetch<EngagementStats>('GET', `/api/engagements/stats${qs}`)
  },

  async update(
    id: string,
    patch: Partial<Pick<EngagementSummary, 'name' | 'client_name' | 'scope'> & { notes: string | null }>,
  ): Promise<void> {
    await apiFetch('PATCH', `/api/engagements/${id}`, patch)
  },

  async delete(id: string): Promise<void> {
    await apiFetch('DELETE', `/api/engagements/${id}`)
  },

  async updateAttackReaction(
    engagementId: string,
    runId: string,
    observed_reaction: string | null,
  ): Promise<{ id: string; observed_reaction: string | null; reviewed_by: string | null; reviewed_at: string | null }> {
    const res = await apiFetch<{
      run: { id: string; observed_reaction: string | null; reviewed_by: string | null; reviewed_at: string | null }
    }>(
      'PATCH',
      `/api/engagements/${engagementId}/simulations/${runId}/reaction`,
      { observed_reaction },
    )
    return res.run
  },
}
